import { motion } from "framer-motion";
import { Truck, MessageCircle, Phone, Mail, MapPin } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const footerLinks = {
    company: [
      { label: "About Us", href: "#" },
      { label: "How It Works", id: "how-it-works" },
      { label: "Pricing", id: "pricing" },
      { label: "Contact", id: "contact" },
    ],
    legal: [
      { label: "Terms of Service", href: "#" },
      { label: "Privacy Policy", href: "#" },
      { label: "Cookie Policy", href: "#" },
    ],
    resources: [
      { label: "FAQ", href: "#" },
      { label: "Blog", href: "#" },
      { label: "Load Board Tips", href: "#" },
    ],
  };

  return (
    <footer className="bg-primary noise-overlay relative overflow-hidden" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <span className="font-heading text-2xl font-bold text-white tracking-tight">
                  MyLane
                </span>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Smart dispatch service for owner-operators. 
                Keep more of what you earn.
              </p>
            </motion.div>

            {/* Contact Info */}
            <div className="space-y-3">
              <a
                href="https://wa.me/15146993007"
                className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors"
                data-testid="footer-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                <span>WhatsApp: +1 (514) 699-3007</span>
              </a>
              <a
                href="tel:+15146993007"
                className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-5 h-5" />
                <span>+1 (514) 699-3007</span>
              </a>
              <a
                href="mailto:info@mylane.io"
                className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-5 h-5" />
                <span>info@mylane.io</span>
              </a>
              <div className="flex items-center gap-3 text-slate-400">
                <MapPin className="w-5 h-5" />
                <span>USA & Canada</span>
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading text-lg font-bold text-white uppercase tracking-wide mb-4">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  {link.id ? (
                    <button
                      onClick={() => scrollToSection(link.id)}
                      className="text-slate-400 hover:text-accent transition-colors"
                      data-testid={`footer-link-${link.id}`}
                    >
                      {link.label}
                    </button>
                  ) : (
                    <a
                      href={link.href}
                      className="text-slate-400 hover:text-accent transition-colors"
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-heading text-lg font-bold text-white uppercase tracking-wide mb-4">
              Legal
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-slate-400 hover:text-accent transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-heading text-lg font-bold text-white uppercase tracking-wide mb-4">
              Resources
            </h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-slate-400 hover:text-accent transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <div className="mt-8">
              <button
                onClick={() => scrollToSection("contact")}
                className="bg-accent hover:bg-accent/90 text-white font-bold uppercase tracking-wide px-6 py-3 rounded-md transition-all inline-flex items-center gap-2"
                data-testid="footer-cta"
              >
                <MessageCircle className="w-5 h-5" />
                Start Now
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} MyLane Dispatch. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-slate-500 text-sm">
                Serving owner-operators across USA & Canada
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/5 rounded-full blur-3xl" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
    </footer>
  );
};

export default Footer;
